import { createContext, useEffect, useState, useRef } from 'react'
import { Amplify, Auth } from 'aws-amplify'
import _ from 'lodash'
import Swal from 'sweetalert2'

import awsConfig from './config/AwsConfig'
import {
  sessionLength,
  validateSessionLength,
  userGroups
} from './config/constants'

import Routers from './Router'

import PageNotFound from './containers/PageNotFound/PageNotFound'

import Spinner from './components/Spinner/Spinner'

import './sass/aws.css'
import {
  saveUserSession,
  validateUserSession
} from './store/services/UserSession.services'

Amplify.configure(awsConfig)

export const AuthContext = createContext<{
  user: any
}>({ user: null })

function App() {
  const [user, setUser] = useState(null)

  const validateSessionTimer = useRef<NodeJS.Timeout | null>(null)

  let tokenTimeoutTimer: any = null

  async function routeToLogin() {
    Auth.federatedSignIn()
  }

  function signOut() {
    setUser(null)

    sessionStorage.removeItem('auth-token')
    sessionStorage.removeItem('refresh-token')

    Auth.signOut()

    routeToLogin()
  }

  useEffect(() => {
    getUser()
  }, [])

  useEffect(() => {
    validatingUserSession()
  }, [])

  async function getUser() {
    let userData

    try {
      userData = await Auth.currentAuthenticatedUser()
    } catch (error) {
      routeToLogin()
    }

    sessionStorage.setItem(
      'auth-token',
      userData.signInUserSession.idToken.jwtToken
    )

    sessionStorage.setItem(
      'refresh-token',
      userData.signInUserSession.refreshToken.token
    )

    sessionStorage.setItem(
      'groups',
      userData.signInUserSession.idToken.payload['cognito:groups']
    )

    sessionStorage.setItem(
      'userSub',
      userData.signInUserSession.idToken.payload.sub
    )

    sessionStorage.setItem(
      'userTokenSessionId',
      userData.signInUserSession.idToken.payload.origin_jti
    )

    setUser(userData)

    if (!tokenTimeoutTimer) {
      tokenTimeoutTimer = setTimeout(() => {
        stopValidatingUserSession()
        Swal.fire({
          title: 'Hello!',
          text: 'Your session is about to expire.',
          icon: 'warning',
          confirmButtonText: 'Continue',
          showDenyButton: true,
          denyButtonText: 'Sign Out'
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload()
          } else if (result.isDenied) {
            signOut()
          }
        })
      }, sessionLength)
    }

    await saveCurrentUserSession(userData)
  }

  async function validatingUserSession() {
    if (validateSessionTimer.current) {
      return
    }

    validateSessionTimer.current = setInterval(async () => {
      const isValidUser = await validateUserSession()

      if (!isValidUser) {
        stopValidatingUserSession()
      }
    }, validateSessionLength)
  }

  const stopValidatingUserSession = () => {
    if (validateSessionTimer.current) {
      clearInterval(validateSessionTimer.current)
      validateSessionTimer.current = null
    }
  }

  async function saveCurrentUserSession(userData: any) {
    await saveUserSession({
      userSub: userData.signInUserSession.idToken.payload.sub,
      activeSessionId: userData.signInUserSession.idToken.payload.origin_jti,
      issuedAt: userData.signInUserSession.idToken.payload.iat,
      expiresAt: userData.signInUserSession.idToken.payload.exp
    })
  }

  function getGroups(groups: any) {
    const groupsArray = groups.split(','),
      userGroupsValues = _.map(userGroups, 'value')

    if (groupsArray && userGroupsValues.some((e) => groupsArray.includes(e))) {
      return true
    }

    return false
  }

  return (
    <AuthContext.Provider value={{ user }}>
      {user ? (
        getGroups(sessionStorage.getItem('groups')) ? (
          <Routers />
        ) : (
          <PageNotFound />
        )
      ) : (
        <Spinner />
      )}
    </AuthContext.Provider>
  )
}

export default App
